import { Injectable } from '@angular/core';
import { AclAssetPermission } from '@y42/acl/models';
import {
  DatasourceCTypeModel,
  DatasourceQuery,
  DatasourceTableUIModel,
  DatasourceTypeQuery,
  DatasourceTypesContentfulService,
  UNKNOWN_DATASOURCE_TYPE_MODEL,
} from '@y42/datasource';
import { Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import { Observable } from 'rxjs';
import { SqlModelingTableQuery } from '../datasource-table';
import { SqlModelingTableJobQuery } from '../datasource-table-job';
import { SqlModelingGitService } from './sql-modeling-git.service';
import { SqlModelingState, SqlModelingStoreModel, SqlModelingUIModel } from './sql-modeling.model';
import { SqlModelingStore } from './sql-modeling.store';

@Injectable({ providedIn: 'root' })
export class SqlModelingQuery extends DatasourceQuery<SqlModelingState, SqlModelingUIModel> {
  constructor(
    protected readonly store: SqlModelingStore,
    protected readonly datasourceTypeQuery: DatasourceTypeQuery,
    protected readonly datasourceTableQuery: SqlModelingTableQuery,
    protected readonly datasourceTableJobQuery: SqlModelingTableJobQuery,
    private readonly featureAclQuery: FeatureAclQuery,
    private readonly modelingGitService: SqlModelingGitService,
    private readonly datasourceTypesContentfulService: DatasourceTypesContentfulService,
  ) {
    super(Y42FeatureType.SqlModel, store, datasourceTypeQuery, datasourceTableQuery, {
      toUIModel: (storeModel) => {
        const { last_used, starred } = this.getValue();
        // Don't remove optional chaining operator, in some very rare cases settings can be undefined
        const $schema = (storeModel.settings as SqlModelingUIModel | undefined)?.$schema ?? '';
        const typeModel = this.datasourceTypeQuery.findModelBySchema($schema) ?? UNKNOWN_DATASOURCE_TYPE_MODEL;

        const resourceType = Y42FeatureToAclAssetResourceType[this.featureType];
        assertNonNullable(resourceType);

        const sqlModelHasReadPermission = !!this.featureAclQuery.getPermission({
          resourceType,
          assetPath: storeModel.$path,
          permission: AclAssetPermission.Read,
        })?.allowed;

        const tables = storeModel.$children
          .map((id) => this.datasourceTableQuery.getUIEntity(id))
          .filter(isNotNullish)
          .map((table) => ({
            ...table,
            hasReadPermission: sqlModelHasReadPermission,
          }));

        return {
          ...storeModel,
          featureType: this.featureType,
          typeIcon: typeModel.logo,
          typeLabel: typeModel.displayName,
          children: tables,
          tables,
          typeModel,
          last_used: last_used[storeModel.$path] || null,
          starred: !!starred[storeModel.$path],
          hasReadPermission: sqlModelHasReadPermission,
          ...this.enrichWithSqlContent(storeModel, tables),
        };
      },
    });
  }

  private readonly sqlQueriesCache: Record<string, string> = {};

  readonly types$: Observable<DatasourceCTypeModel[] | null> = this.datasourceTypesContentfulService.data$;

  private enrichWithSqlContent(storeModel: SqlModelingStoreModel, tables: DatasourceTableUIModel[]) {
    return {
      getSqlModel: () =>
        Promise.all(
          (tables || []).map(async (table) => {
            let sql = '';
            const sqlQueryFilePath = this.getSqlModelFilePath(storeModel.$name, table.$name);
            try {
              sql = (await this.modelingGitService.readFile(sqlQueryFilePath)) || '';
            } catch (err) {
              sql = '';
            }
            this.sqlQueriesCache[sqlQueryFilePath] = sql;

            return {
              table_name: table.$name,
              sql,
            };
          }),
        ),
      setSqlQuery: async (tableName: string, sqlQuery: string) => {
        const sqlQueryFilePath = this.getSqlModelFilePath(storeModel.$name, tableName);
        const cachedSqlQuery = this.sqlQueriesCache[sqlQueryFilePath];
        if (isNullish(cachedSqlQuery) || cachedSqlQuery !== sqlQuery) {
          // sql cache missed or changed query - we need to write content
          await this.modelingGitService.writeSqlContentToFile(sqlQueryFilePath, sqlQuery);
          this.sqlQueriesCache[sqlQueryFilePath] = sqlQuery;
        }
        // sql cache hit - same query, no need to update
      },
    };
  }

  private getSqlModelFilePath(modelName: string, tableName: string) {
    return this.modelingGitService.getPath(modelName, `${tableName}/sql-model.sql`);
  }
}
